import React, { useState, useEffect, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useQuery, useLazyQuery } from '@apollo/client';
import { SVGIcon } from '@superops/super-icons';

import {
  DromoUploader,
  ALL_SHARD_MODULES,
  getTranslatedText
} from '@nile/components';
import { GET_ALL_FIELDS_V2 } from '@nile/banyan/src/Graphql/Mutations/ticket';
import { AccountClient, GET_ALL_TECH_GROUP } from '@nile/graphql';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';
import { GET_ALL_CLIENT_DATA } from '../dromo';

import {
  getClientImportFields,
  shapeOptions,
  getQuery,
  callQuery
} from '../Fields/clientFields';

import {
  getValidators,
  getFieldType,
  getSelectOptions,
  isMultiSelect,
  splitter,
  duplicateChecker,
  intlConverter
} from '../utils/helpers';

// Constants
const rowIndicesWithDuplicates = new Set();

const ClientUploader = ({
  onResults,
  onCancel,
  fields: propFields,
  validationProps = {},
  userToken,
  intl,
  activator
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [clientCustomFields, setClientCustomFields] = useState([]);
  const [allTechGroups, setAllTechGroups] = useState([]);
  const [usedValues, setUsedValues] = useState({
    techGroups: [],
    emailDomains: [],
    primaryContactEmails: []
  });

  const usedDataRef = useRef();
  usedDataRef.current = usedValues;
  const techGroupsByRef = useRef();
  techGroupsByRef.current = allTechGroups;

  const [
    getCustomFields,
    { loading: cfLoading, error: cfError, data: customFields }
  ] = useLazyQuery(GET_ALL_FIELDS_V2, {
    client: AccountClient,
    variables: { input: ALL_SHARD_MODULES.client },
    fetchPolicy: 'cache-and-network',
    skip: !!validationProps?.customFields,
    onCompleted({ getAllFields }) {
      if (getAllFields) setClientCustomFields(getAllFields);

      const customFieldsToAdd = [];
      getAllFields?.forEach((cf) => {
        if (cf?.fieldCategory === 'CUSTOM') {
          customFieldsToAdd.push({
            label: cf.label,
            key: cf.columnName,
            type: getFieldType(cf),
            selectOptions: getSelectOptions(cf, true),
            validators: getValidators(cf)
          });
        }
      });

      setFields(intlConverter(intl, [...fields, ...customFieldsToAdd]));
    }
  });

  const {
    data: technicianGroups,
    loading: techGroupsLoading,
    error: techGroupsError
  } = useQuery(GET_ALL_TECH_GROUP, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    skip: !!validationProps?.allTechGroups,
    onCompleted({ getTechnicianGroups }) {
      if (getTechnicianGroups) {
        setAllTechGroups(getTechnicianGroups.map(({ name }) => name));
      }
    }
  });

  const {
    data: validationData,
    loading: validationDataLoading,
    error: validationDataError
  } = useQuery(GET_ALL_CLIENT_DATA, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    variables: { listInfo: {} },
    skip: !!validationProps?.usedValues,
    onCompleted({ getClientList }) {
      if (getClientList) {
        let techGroups = [];
        let emailDomains = [];
        let primaryContactEmails = [];
        getClientList.clients.forEach((client) => {
          techGroups = [
            ...techGroups,
            ...client.technicianGroups.map((techGroup) => ({
              ...techGroup,
              clientName: client.name
            }))
          ];

          emailDomains = [
            ...emailDomains,
            ...client.emailDomains.map((emailDomain) => ({
              emailDomain,
              clientName: client.name
            }))
          ];

          primaryContactEmails = [
            ...primaryContactEmails,
            {
              ...client.primaryContact,
              clientName: client.name
            }
          ];
        });

        setUsedValues({
          techGroups,
          emailDomains,
          primaryContactEmails
        });
      }
    }
  });

  useEffect(() => {
    const { allTechGroups, usedValues, customFields } = validationProps;
    if (allTechGroups) {
      setAllTechGroups(allTechGroups);
    }
    if (usedValues) {
      setUsedValues(usedValues);
    }
    if (customFields) {
      setClientCustomFields(customFields);
    }
  }, [validationProps]);

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      let initFields = [...getClientImportFields()];

      const selectFields = getClientImportFields()?.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields(intlConverter(intl, initFields));
          getCustomFields({
            variables: {
              input: ALL_SHARD_MODULES.client
            }
          });
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  const multiselectFieldValidator = (keyName, values) => {
    const fieldValues = splitter(values);
    const associatedCustomField = clientCustomFields.find(
      ({ columnName }) => columnName === keyName
    );
    let isError = false;
    fieldValues.forEach((value) => {
      if (!isError && !associatedCustomField.options.includes(value))
        isError = true;
    });
    return isError;
  };

  const customFieldValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };
    const multiSelectFields = clientCustomFields
      .filter((cf) => isMultiSelect(cf))
      .map(({ columnName }) => columnName);

    multiSelectFields.forEach((field) => {
      if (
        row[field]?.value?.trim() &&
        multiselectFieldValidator(field, row[field].value)
      ) {
        newRecord.row[field].info = [
          {
            message: getTranslatedText(
              intl,
              'dromo.custom.customFields.optionNotFound'
            ),
            level: 'error'
          }
        ];
      } else {
        if (newRecord?.row?.[field]) newRecord.row[field].info = [];
      }
    });

    return newRecord;
  };

  const primaryContactPhoneNumberValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };

    if (!row.primaryContactPhoneNumber?.value || !row.name?.value) {
      if (row.primaryContactPhoneNumber)
        newRecord.row.primaryContactPhoneNumber.info = [];
      return newRecord;
    }

    const client = row.name.value;
    const contactUsed = usedDataRef.current.primaryContactEmails.find(
      (contact) => contact.contactNumber === row.primaryContactPhoneNumber.value
    );

    // Checking if contact is already used by another client in database
    if (
      contactUsed &&
      client.toLowerCase() !== contactUsed.clientName?.toLowerCase()
    ) {
      newRecord.row.primaryContactPhoneNumber.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.primaryContact.inUse'),
          level: 'error'
        }
      ];
      return newRecord;
    }

    newRecord.row.primaryContactPhoneNumber.info = [];

    return newRecord;
  };

  const primaryContactEmailValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };

    if (!row.primaryContactEmail?.value || !row.name?.value) {
      if (row.primaryContactEmail) newRecord.row.primaryContactEmail.info = [];
      return newRecord;
    }

    const client = row.name.value;
    const contactUsed = usedDataRef.current.primaryContactEmails.find(
      (contact) => contact.email === row.primaryContactEmail.value
    );

    // Checking if contact is already used by another client in database
    if (
      contactUsed &&
      client.toLowerCase() !== contactUsed.clientName?.toLowerCase()
    ) {
      newRecord.row.primaryContactEmail.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.primaryContact.inUse'),
          level: 'error'
        }
      ];
      return newRecord;
    }

    newRecord.row.primaryContactEmail.info = [];

    return newRecord;
  };

  const emailDomainRowHookValidator = ({ row, index }, mode) => {
    const newRecord = { row, index };

    if (!row.emailDomains?.value || !row.name?.value) {
      if (row.emailDomains) newRecord.row.emailDomains.info = [];
      return newRecord;
    }

    const client = row.name.value;

    const emailDomainsEntered = splitter(row.emailDomains?.value);

    const emailDomainUsed = (domain) =>
      usedDataRef.current.emailDomains.find(
        ({ emailDomain }) => emailDomain === domain
      );

    // In init mode if column values are duplicated set an error
    if (mode === 'init' && rowIndicesWithDuplicates.has(index)) {
      newRecord.row.emailDomains.info = [
        {
          message: getTranslatedText(
            intl,
            'dromo.custom.emailDomain.duplicated'
          ),
          level: 'error'
        }
      ];
      return newRecord;
    }

    // Duplicate check for domains entered in cell
    const duplicatesFound = duplicateChecker(emailDomainsEntered, true);
    if (duplicatesFound) {
      newRecord.row.emailDomains.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.emailDomain.repeated'),
          level: 'error'
        }
      ];
      return newRecord;
    }

    // Checking if domain is already used by another client in database
    const domainsWithError = [];
    for (let i = 0; i < emailDomainsEntered.length; i++) {
      const domain = emailDomainsEntered[i];
      const domainUsed = emailDomainUsed(domain);
      if (
        domainUsed &&
        client.toLowerCase() !== domainUsed.clientName?.toLowerCase()
      ) {
        domainsWithError.push(domain);
      }
    }
    if (domainsWithError.length > 0) {
      newRecord.row.emailDomains.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.emailDomain.inUse', {
            domainsInUse: domainsWithError.join(', ')
          }),
          level: 'error'
        }
      ];
      return newRecord;
    }

    newRecord.row.emailDomains.info = [];

    return newRecord;
  };

  const techGroupRowHookValidator = ({ row, index }) => {
    const newRecord = { row, index };

    if (!row.technicianGroups?.value || !row.name?.value) {
      if (row.technicianGroups) newRecord.row.technicianGroups.info = [];
      return newRecord;
    }

    const techGroupsEntered = splitter(row.technicianGroups?.value);

    // Duplicate check for domains entered in cell
    const duplicatesFound = duplicateChecker(techGroupsEntered, true);
    if (duplicatesFound) {
      newRecord.row.technicianGroups.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.techGroup.repeated'),
          level: 'error'
        }
      ];
      return newRecord;
    }

    // Check if technician group exists in database
    const techGroupsNotAvailable = [];
    for (let i = 0; i < techGroupsEntered.length; i++) {
      const techGroup = techGroupsEntered[i];
      const techGroupAvailable = techGroupsByRef.current.find(
        (group) => group === techGroup
      );
      if (!techGroupAvailable) {
        techGroupsNotAvailable.push(techGroup);
      }
    }
    if (techGroupsNotAvailable.length > 0) {
      newRecord.row.technicianGroups.info = [
        {
          message: getTranslatedText(intl, 'dromo.custom.techGroup.inUse', {
            techGroupsInUse: techGroupsNotAvailable.join(', ')
          }),
          level: 'error'
        }
      ];
      return newRecord;
    }

    newRecord.row.technicianGroups.info = [];

    return newRecord;
  };

  if (loading || cfLoading || validationDataLoading || techGroupsLoading)
    return <Loader loading />;

  if (validationDataError || techGroupsError || error) return null;

  const columnHooks = [
    {
      fieldName: 'emailDomains',
      callback: (values) => {
        const emailDomainMap = {};

        values.forEach(({ value, index }) => {
          const domains = splitter(value);
          const domainsInCell = new Set();
          domains.forEach((domain) => {
            if (domain) domainsInCell.add(domain.toLowerCase());
          });
          domainsInCell.forEach((domain) => {
            if (!emailDomainMap[domain]) {
              emailDomainMap[domain] = true;
            } else {
              rowIndicesWithDuplicates.add(index);
            }
          });
        });

        return values;
      }
    }
  ];

  return (
    <DromoUploader
      settings={{
        importIdentifier: 'CLIENT',
        title: getTranslatedText(intl, 'dromo.client.import.title')
      }}
      fields={propFields || fields}
      rowHooks={[
        (row) => row,
        customFieldValidatorRowHook,
        emailDomainRowHookValidator,
        techGroupRowHookValidator
      ]}
      columnHooks={columnHooks}
      onResults={(response, metadata) => {
        setResults(response);
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="ClientLogo" />
            </div>
            <FormattedMessage id="dromo.csv.client.import" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default injectIntl(ClientUploader);
